import React from 'react'
import withStyles from '@material-ui/core/styles/withStyles'
import { emphasize, lighten } from '@material-ui/core/styles/colorManipulator'
import Icon from '@material-ui/core/Icon'
import Chip from '@material-ui/core/Chip'
import Link from 'react-router-dom/Link'
import classNames from 'classnames'
import Colors from '~shared/assets/styles/colors'
import { withLanguage } from '~src/LanguageContext'

const styles = theme => {
  const { unit } = theme.spacing
  const backgroundColor = lighten(theme.palette.secondary.main, 0.35)

  return {
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: unit * 0.5,
      alignItems: 'flex-start',
      minHeight: unit * 3,
      width: '100%',
    },
    chipRow: {
      display: 'flex',
      flexDirection: 'column',
      gap: unit * 0.25,
      alignItems: 'flex-start',
      width: '100%',
    },
    root: {
      display: 'flex',
      justifyContent: 'flex-start',
      backgroundColor: backgroundColor,
      height: unit * 2.5,
      fontSize: '0.7rem',
      width: '100%',
      maxWidth: '265px',
    },
    variantSmall: {
      height: unit * 2,
      fontSize: '0.75rem',
    },
    chipLabel: {
      overflow: 'hidden',
      paddingLeft: unit * 0.75,
      paddingRight: unit * 0.75,
      width: '100%',
    },
    labelContainer: {
      display: 'flex',
      alignItems: 'center',
      overflow: 'hidden',
      width: '100%',
    },
    label: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      flex: 1,
      minWidth: 0,
    },
    collectionPrefix: {
      fontSize: '0.7rem',
      opacity: 0.7,
      marginRight: unit * 0.5,
      fontWeight: 400,
      flexShrink: 0,
    },
    clickable: {
      '&:hover': {
        backgroundColor: emphasize(backgroundColor, 0.12),
        transform: 'translateY(-1px)',
      },
      '&:focus': {
        backgroundColor: backgroundColor,
      },
      '&:active': {
        backgroundColor: backgroundColor,
        transform: 'translateY(0)',
      },
      transition: 'all 0.2s ease-in-out',
    },
    icon: {
      color: Colors.Grey,
      marginRight: unit * 0.25,
      fontSize: '1rem',
      flexShrink: 0,
    },
    emptyState: {
      color: 'rgba(0,0,0,0.4)',
      fontStyle: 'italic',
      fontSize: '0.875rem',
      padding: 0,
    },
  }
}

const COLLECTION_CONFIG = {
  tobaccoshoppers: {
    label: 'Tobacco Shoppers',
    icon: 'article',
    detail: 'tobaccoshoppers',
  },
  quizs: {
    label: 'Quizzes',
    icon: 'quiz',
    detail: 'quizs',
  },
  surveys: {
    label: 'Surveys',
    icon: 'poll',
    detail: 'surveys',
  },
  games: {
    label: 'Games',
    icon: 'games',
    detail: 'games',
  },
  mod_tasks: {
    label: 'Tasks',
    icon: 'camera_alt',
    detail: 'mod_tasks',
  },
}

const MultiReferenceChip = withLanguage(
  withStyles(styles)(
    ({
      classes,
      className,
      value = {},
      settings = {},
      variant = 'normal',
      selectedLanguage,
      ...rest
    }) => {
      if (!value || typeof value !== 'object' || Object.keys(value).length === 0) {
        return <div className={classes.emptyState}>No failed activities</div>
      }

      const renderSingleChip = (collectionName, itemData, index) => {
        const config = COLLECTION_CONFIG[collectionName] || {
          label: collectionName,
          icon: 'link',
          detail: collectionName,
        }

        let processedItemData = itemData

        if (typeof itemData === 'string') {
          processedItemData = {
            _id: itemData,
            _name: `${config.label} (${itemData})`,
            title: `${config.label} (${itemData})`,
          }
        }

        if (!processedItemData || !processedItemData._id) {
          return null
        }

        const { _name, title = _name, _id } = processedItemData

        const linkProps = {
          component: Link,
          to: `/~/${config.detail}/${_id}`,
        }

        const dir = selectedLanguage && selectedLanguage.rtl ? 'auto' : undefined

        return (
          <Chip
            key={`${collectionName}_${_id}_${index}`}
            classes={{
              root: classes.root,
              label: classes.chipLabel,
              clickable: classes.clickable,
            }}
            className={classNames(className, {
              [classes.variantSmall]: variant === 'small',
            })}
            clickable
            onClick={e => {
              e.stopPropagation()
            }}
            label={
              <div className={classes.labelContainer}>
                <Icon className={classes.icon} fontSize="small">
                  {config.icon}
                </Icon>
                {variant !== 'small' && (
                  <span className={classes.collectionPrefix}>{config.label}:</span>
                )}
                <span dir={dir} className={classes.label}>
                  {title || _name || _id}
                </span>
              </div>
            }
            {...linkProps}
            {...rest}
          />
        )
      }

      return (
        <div className={classes.chipRow}>
          {Object.entries(value).map(([collectionName, itemData]) =>
            Array.isArray(itemData)
              ? itemData.map((item, index) => renderSingleChip(collectionName, item, index))
              : renderSingleChip(collectionName, itemData, 0),
          )}
        </div>
      )
    },
  ),
)

export default MultiReferenceChip
